import React, { PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';

import { pathToRegexp } from 'path-to-regexp';

import { LoadingWrapper } from '../../components/LoadingWrapper/LoadingWrapper';
import { routes } from '../../constants/routing';
import { languageHooks } from '../../hooks/languageHooks/languageHooks';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { navigationHooks } from '../../hooks/navigationHooks/navigationHooks';
import { routerBuildHooks } from '../../hooks/routerBuildHooks/routerBuildHooks';
import { routerHooks } from '../../hooks/routerHooks/routerHooks';
import { AccountMessages } from '../../types/Messages';
import { UserResponse } from '../../types/apiTypes';
import { Message } from '../../types/common';

type UseGetRedirectParams = {
  pathname: string;
  userInfo: UserResponse | null;
};

const AUTHORIZED_USER_BLACK_LIST = [
  routes.accountIdentifyPageRoute,
  routes.accountRegisterPageRoute,
  routes.loginPageRoute,
  routes.forgotPasswordPageRoute,
  routes.newPasswordPageRoute,
];

const UNAUTHORIZED_USER_BLACK_LIST = [
  routes.accountOverviewPageRoute,
  routes.accountOrdersPageRoute,
  routes.accountDataPageRoute,
  routes.accountAddressBookPageRoute,
];

type UseGetBlackListParams = {
  authorizedBlackList: Message[];
  unauthorizedBlackList: Message[];
};

export const hooks = {
  useGetBlackLists({
    authorizedBlackList,
    unauthorizedBlackList,
  }: UseGetBlackListParams) {
    const intl = useIntl();
    const languageParamRegex = languageHooks.useGetLanguageParamRegex();
    const accountIdentifyRedirect = navigationHooks.useAccountIdentifyRedirect();
    const accountOverviewRedirect = navigationHooks.useAccountOverviewRedirect();
    return React.useMemo(() => ({
      authorizedUserBlackList: authorizedBlackList.map((route) => intl.formatMessage(route, {
        languageParamRegex,
      })),
      unauthorizedUserBlackList: unauthorizedBlackList.map((route) => intl.formatMessage(route, {
        languageParamRegex,
      })),
      accountIdentifyRedirect,
      accountOverviewRedirect,
    }), [authorizedBlackList, unauthorizedBlackList,
      intl, languageParamRegex, accountOverviewRedirect, accountIdentifyRedirect]);
  },

  useGetRedirect({
    userInfo,
    pathname,
  }: UseGetRedirectParams) {
    const {
      authorizedUserBlackList,
      unauthorizedUserBlackList,
      accountIdentifyRedirect,
      accountOverviewRedirect,
    } = hooks.useGetBlackLists({
      authorizedBlackList: AUTHORIZED_USER_BLACK_LIST,
      unauthorizedBlackList: UNAUTHORIZED_USER_BLACK_LIST,
    });

    const getFoundPath = hooks.useGetPathFromBlackListCallback();

    if (!userInfo) {
      return null;
    }

    if (!userInfo?.user && getFoundPath(unauthorizedUserBlackList, pathname)) {
      return accountIdentifyRedirect;
    }

    if (userInfo?.user && getFoundPath(authorizedUserBlackList, pathname)) {
      return accountOverviewRedirect;
    }

    return null;
  },

  useSubjectStates() {
    const userInfo = messageHooks.useSubjectState(AccountMessages.AccountUserInfo);
    const userInfoLoading = messageHooks.useSubjectState(AccountMessages.AccountUserInfoLoading);
    return {
      userInfo,
      userInfoLoading,
    };
  },

  useGetPathFromBlackListCallback () {
    return React.useCallback((blackList: string[], pathname: string) => {
      const pathNameWithSlashInTheEnd = pathname.endsWith('/') ? pathname : `${pathname}/`;
      return blackList.find(el => {
        const regExp = pathToRegexp(el, [], {
          sensitive: true,
        });
        if (regExp.exec(pathNameWithSlashInTheEnd)) return el;
        return undefined;
      });
    }, []);
  },

  useAccountRedirect(redirectPath: string | null) {
    const redirect = routerBuildHooks.useRedirectCallback();

    React.useEffect(() => {
      redirectPath && redirect(redirectPath);
    }, [redirect, redirectPath]);
  },
};

export type Props = {
};

export const AccountRedirectContainer = React.memo<PropsWithChildren<Props>>((props) => {
  const pathname = routerHooks.usePathName();
  const { userInfo, userInfoLoading } = hooks.useSubjectStates();
  const redirectPath = hooks.useGetRedirect({ userInfo, pathname });
  hooks.useAccountRedirect(redirectPath);

  if (!userInfo || userInfoLoading) {
    return (
      <LoadingWrapper
        loadingBarFixedAtTheCenterOfTheScreen
        loading={userInfoLoading}
      />
    );
  }

  return !redirectPath ? <>{props.children}</> : null;
});

AccountRedirectContainer.displayName = 'AccountRedirectContainer';
